import { Button } from '@/components/ui/button'
import { FreePreviewCreditCard } from '@/components/marketing/tools/free-preview'
import {
  isAppSumoPlan,
  isTopPlan as isTopPlanSlug,
} from '@/lib/billing/top-plan'

/**
 * The rendered credit card, with the balance already resolved.
 *
 * Separate module from `credit-badge.tsx` on purpose: this file imports only
 * other presentation, so a test can mount it and click its three CTAs in
 * jsdom. Reaching it through the data shell instead pulls in the SWR and
 * analytics layers, and `analytics/track.ts` loads `posthog-js`, which touches
 * a bare `location` at import time and throws before any test body runs.
 */
export function CreditBadgeCard({
  balance,
  cost,
  costLabel = 'This costs',
  onBuyCredits,
  onUpgrade,
  planBlocked,
  planName,
  planSlug = null,
  previewMode,
}: {
  balance: number
  cost: number
  /** Leading words of the price sentence — the video studios say "This clip". */
  costLabel?: string
  /** Opens a one-time credit pack purchase. Shown instead of "Upgrade" on
   *  plans with no subscription above them (AppSumo lifetime tiers). */
  onBuyCredits?: () => void
  onUpgrade: () => void
  planBlocked: boolean
  planName: string | null
  planSlug?: string | null
  previewMode: boolean
}) {
  // The free-watermarked-preview experiment: this org has one free run. Say
  // exactly what it gets (a watermarked preview, no credits spent) and what
  // removes the watermark, before the prompt is written.
  if (previewMode) return <FreePreviewCreditCard onUpgrade={onUpgrade} />

  // PLAN GATE BEATS PRICE. Generation is a paid-plan capability — the server
  // asserts it for all 48 tools before it ever looks at the balance — so for
  // an org without it, a balance and a per-job price are both true and both
  // beside the point. Showing them made the Free grant read as spendable here:
  // 2,000 credits against a 325-credit job looks affordable right up until the
  // button rejects you on an axis the page never mentioned.
  //
  // Say the actual requirement instead, before any prompt gets written.
  if (planBlocked) {
    return (
      <div className="border-border bg-muted/50 rounded-xl border p-4">
        <div className="flex items-center justify-between gap-2">
          <div>
            <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
              Paid plans only
            </p>
            <p className="text-foreground mt-1 text-sm font-semibold">
              Generating needs a plan{' '}
              <span className="text-muted-foreground font-normal">
                · from $19/mo
              </span>
            </p>
          </div>
          <Button size="sm" className="rounded-full" onClick={onUpgrade}>
            See plans
          </Button>
        </div>
        <p className="text-muted-foreground mt-2 text-xs">
          Your {balance.toLocaleString()} credits cover chat and agent work on
          the {planName ?? 'Free'} plan; running the generators is a paid
          feature.
        </p>
      </div>
    )
  }

  const insufficient = balance < cost
  // Pro / Enterprise / AppSumo lifetime are the top tiers — nothing to
  // upgrade to. Lifetime buyers top up with a credit pack instead.
  const isTopPlan = isTopPlanSlug(planSlug, planName)
  const buysCredits = !!onBuyCredits && isAppSumoPlan(planSlug)

  return (
    <div className="border-border bg-muted/50 rounded-xl border p-4">
      <div className="flex items-center justify-between gap-2">
        <div>
          <p className="text-muted-foreground text-[10px] tracking-[0.3em] uppercase">
            Your credits
          </p>
          <p className="text-foreground mt-1 text-sm font-semibold">
            {balance.toLocaleString()}{' '}
            <span className="text-muted-foreground font-normal">
              {planName ? `· ${planName} plan` : 'credits'}
            </span>
          </p>
        </div>
        {buysCredits ? (
          <Button
            size="sm"
            variant={insufficient ? 'default' : 'outline'}
            className="rounded-full"
            onClick={onBuyCredits}
          >
            Buy credits
          </Button>
        ) : (
          !isTopPlan && (
            <Button
              size="sm"
              variant={insufficient ? 'default' : 'outline'}
              className="rounded-full"
              onClick={onUpgrade}
            >
              Upgrade
            </Button>
          )
        )}
      </div>
      <p
        className={`mt-2 text-xs ${insufficient ? 'text-amber-600' : 'text-muted-foreground'}`}
      >
        {costLabel} ~{cost.toLocaleString()} credits
        {insufficient
          ? buysCredits
            ? ' — buy a credit pack to keep generating.'
            : isTopPlan
              ? ' — credits refresh on your renewal date.'
              : ' — upgrade to generate.'
          : '.'}
      </p>
    </div>
  )
}
